import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Marquee } from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";
import { ConsultationForm } from "@/components/ConsultationForm";
import { ProxaviSpotlight } from "@/components/ProxaviSpotlight";
import { ReviewsStrip } from "@/components/ReviewsStrip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SITE, IMAGES, SERVICES, PROCESS_STEPS, FAQ_ITEMS, PRODUCT_KEYS } from "@/config/site";

export default function HomePage() {
  return (
    <>
      <Seo
        title="Tedmo Services — App Development, Cloud & Server Deployment"
        description="Tedmo Services builds custom applications and deploys them to AWS, Azure, Google Cloud, or your own servers — with security built in and support after launch."
        path="/"
      />

      {/* Hero */}
      <section className="pt-16 pb-20 border-b border-line" data-testid="hero-section">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-royal-light text-royal font-mono-sub text-xs uppercase mb-6 font-semibold">
              <span>Global Remote Architecture & Operations</span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-poster text-4xl sm:text-5xl lg:text-6xl font-extrabold text-steel leading-tight"
            >
              <span className="ln"><span>We Build Your Applications.</span></span>
              <span className="ln"><span>We Power Your</span></span>
              <span className="ln"><span className="text-royal">Infrastructure.</span></span>
            </h1>

            <p className="d3-fade text-lg text-ink mt-6 leading-relaxed">
              From custom software to cloud and server deployments, we help
              businesses launch, scale, and maintain secure, reliable technology.
            </p>

            <div className="d3-fade flex flex-wrap items-center gap-4 mt-8">
              <a
                href="#consultation"
                data-testid="hero-cta-consultation"
                className="px-8 py-3.5 rounded bg-steel hover:bg-steel-light text-white text-sm font-semibold transition-colors"
              >
                Request a Consultation
              </a>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="px-6 py-3.5 rounded border border-slate-300 text-slate-700 text-sm font-medium hover:bg-white transition-colors"
              >
                Explore Services
              </Link>
            </div>
          </div>

          <div className="d3-fade rounded-2xl border border-slate-300 overflow-hidden p-2 bg-white shadow-xl">
            <div className="aspect-[21/9] rounded-xl overflow-hidden relative group">
              <img
                src={IMAGES.hero}
                alt="Modern cloud datacenter topology and secure server deployment network"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-8 text-white">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <span className="text-xs font-mono-sub text-blue-300 uppercase">
                      Single Accountability Principle
                    </span>
                    <p className="font-poster font-bold text-xl mt-1">
                      One team to build, deploy, and support your software.
                    </p>
                  </div>
                  <span className="text-xs font-mono-sub bg-white/20 px-3 py-1.5 rounded backdrop-blur">
                    AWS · Azure · GCP · Bare-Metal
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* Services */}
      <section className="py-24 max-w-6xl mx-auto px-6" data-testid="services-section">
        <Reveal className="mb-16">
          <span className="font-mono-sub text-xs text-royal uppercase tracking-wider font-bold">
            Comprehensive Capabilities
          </span>
          <h2 className="font-poster text-3xl md:text-4xl font-bold text-steel mt-2">
            Services Engineered for SMBs & Scale
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
          {SERVICES.map((service, i) => {
            const wide = i === SERVICES.length - 1 && SERVICES.length % 3 === 1;
            return (
            <Reveal key={service.slug} delay={i * 60} className={wide ? "lg:col-span-3" : ""}>
              <Link
                to={`/services#${service.slug}`}
                data-testid={`service-card-${service.slug}`}
                className={`bg-white rounded-xl border border-line p-6 shadow-sm flex flex-col h-full hover:border-royal/40 hover:shadow-md transition-[border-color,box-shadow] ${
                  wide ? "lg:flex-row lg:items-center lg:gap-8" : "justify-between"
                }`}
              >
                {service.image && (
                  <div className={`h-36 rounded-lg mb-4 overflow-hidden shrink-0 ${wide ? "lg:mb-0 lg:w-80" : ""}`}>
                    <img
                      src={service.image}
                      alt={service.imageAlt}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="flex flex-col justify-between flex-1">
                  <div>
                    <h3 className="font-poster font-bold text-lg text-steel">{service.name}</h3>
                    <p className={`text-xs text-mute mt-2 leading-relaxed ${wide ? "lg:text-sm lg:max-w-2xl" : ""}`}>{service.summary}</p>
                  </div>
                  <span className="text-[11px] font-mono-sub text-royal mt-4 font-semibold">
                    {service.tag}
                  </span>
                </div>
              </Link>
            </Reveal>
            );
          })}
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white border-y border-line" data-testid="process-section">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal className="text-center max-w-xl mx-auto mb-14">
            <span className="font-mono-sub text-xs text-royal uppercase tracking-wider font-bold">
              Standard Workflow
            </span>
            <h2 className="font-poster text-3xl font-bold text-steel mt-2">Our Process</h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {PROCESS_STEPS.map((step, i) => (
              <Reveal key={step.name} delay={i * 60}>
                <Link
                  to="/process"
                  data-testid={`process-step-${step.name.toLowerCase()}`}
                  className="block p-5 rounded-lg bg-cloud border border-line h-full hover:border-royal/40 transition-colors"
                >
                  <span className="font-mono-sub text-xs text-royal font-bold">
                    0{i + 1}
                  </span>
                  <h3 className="font-poster font-bold text-base text-steel mt-2">{step.name}</h3>
                  <p className="text-xs text-mute mt-1">{step.summary}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-24 max-w-6xl mx-auto px-6" data-testid="products-section">
        <Reveal className="mb-14">
          <span className="font-mono-sub text-xs text-royal uppercase tracking-wider font-bold">
            Proprietary Technology
          </span>
          <h2 className="font-poster text-3xl md:text-4xl font-bold text-steel mt-2">
            Products by Tedmo
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PRODUCT_KEYS.map((key, i) => {
            const product = SITE.products[key];
            return (
            <Reveal key={key} delay={i * 80}>
              <Link
                to="/products"
                data-testid={`product-card-${key}`}
                className="group bg-white rounded-xl border border-line overflow-hidden shadow-sm flex flex-col h-full hover:border-royal/40 hover:shadow-md transition-[border-color,box-shadow]"
              >
                <div className="relative h-40 overflow-hidden">
                  <img
                    src={IMAGES[key]}
                    alt={`${product.name} platform`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-steel/80 to-transparent" />
                  <span className="absolute left-4 bottom-4 rounded border border-white/20 bg-white/10 px-2.5 py-1 font-mono-sub text-[11px] font-semibold uppercase tracking-wider text-white backdrop-blur">
                    {product.category}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className="font-poster text-xl font-bold text-steel">{product.name}</h3>
                    <span className="text-[11px] font-mono-sub text-mute">{product.status}</span>
                  </div>
                  <p className="text-sm text-ink mt-3 leading-relaxed">{product.description}</p>
                </div>
              </Link>
            </Reveal>
            );
          })}
        </div>
      </section>

      <ProxaviSpotlight />

      <ReviewsStrip />

      {/* About */}
      <section className="py-20 bg-white border-t border-line" data-testid="about-section">
        <Reveal className="max-w-4xl mx-auto px-6 text-center space-y-6">
          <span className="font-mono-sub text-xs text-royal uppercase tracking-wider font-bold">
            About Us
          </span>
          <h2 className="font-poster text-3xl font-bold text-steel">
            One Accountable Team for Software and Infrastructure
          </h2>
          <p className="text-base text-ink leading-relaxed max-w-2xl mx-auto">
            We handle the full journey — from building your application to
            deploying it on cloud platforms or your own servers, with security
            built into the process and ongoing support after launch.
          </p>
          <p className="text-xs font-mono-sub text-mute">
            Serving SMBs and startups remotely across global operating zones.
          </p>
          <Link
            to="/about"
            data-testid="about-read-more-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-royal hover:text-royal-dark transition-colors"
          >
            More about our approach <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="py-20 max-w-3xl mx-auto px-6" data-testid="faq-section">
        <Reveal className="mb-10 text-center">
          <span className="font-mono-sub text-xs text-royal uppercase tracking-wider font-bold">
            Common Questions
          </span>
          <h2 className="font-poster text-3xl font-bold text-steel mt-2">
            Frequently Asked Questions
          </h2>
        </Reveal>
        <Reveal>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger
                  data-testid={`faq-question-${i}`}
                  className="font-poster font-bold text-steel text-left"
                >
                  {item.question}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`faq-answer-${i}`}
                  className="text-sm text-ink leading-relaxed"
                >
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      {/* Consultation */}
      <section id="consultation" className="py-24 bg-steel text-white" data-testid="consultation-section">
        <div className="max-w-2xl mx-auto px-6">
          <Reveal className="text-center mb-10">
            <span className="font-mono-sub text-xs text-royal-bright uppercase tracking-wider">
              Direct Engagement
            </span>
            <h2 className="font-poster text-3xl md:text-4xl font-bold text-white mt-1">
              Request a Consultation
            </h2>
            <p className="text-xs font-mono-sub text-slate-400 mt-2">
              Let us know how we can support your application or infrastructure.
            </p>
          </Reveal>
          <Reveal>
            <ConsultationForm />
          </Reveal>
        </div>
      </section>
    </>
  );
}
